import React, { useState } from 'react';
import { Sparkles, CheckCircle2, ArrowRight, AlertCircle, Loader2 } from 'lucide-react';

export default function Waitlist() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    source: 'website',
    referralCode: '',
  });

  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [responseMessage, setResponseMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (status === 'error') setStatus('idle');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.email.trim()) {
      setErrorMessage('Please enter a valid email address.');
      setStatus('error');
      return;
    }

    setStatus('submitting');
    setErrorMessage('');

    try {
      // Execute Google reCAPTCHA v3 if configured on window
      let captchaToken = null;
      if (window.grecaptcha && process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
        captchaToken = await new Promise((resolve) => {
          window.grecaptcha.ready(() => {
            window.grecaptcha
              .execute(process.env.REACT_APP_RECAPTCHA_SITE_KEY, { action: 'WAITLIST' })
              .then(resolve)
              .catch(() => resolve(null));
          });
        });
      }

      const response = await fetch('/api/waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          metadata: {
            captchaToken,
            screenResolution: `${window.screen.width}x${window.screen.height}`,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to submit. Please try again.');
      }

      setResponseMessage(data.message || "You're on the list!");
      setStatus('success');
    } catch (err) {
      console.error('[Waitlist Submit Error]', err);
      setErrorMessage(err.message || 'Something went wrong. Please try again.');
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-[#F3F2EE] text-[#1e1e1e] flex flex-col justify-center items-center px-4 py-12 font-sans selection:bg-purple-200 selection:text-purple-900">
      {/* Background Decorative Blur */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-40 -right-40 w-96 h-96 bg-purple-200/50 rounded-full blur-3xl opacity-70" />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-sky-200/50 rounded-full blur-3xl opacity-70" />
      </div>

      <div className="w-full max-w-lg z-10">
        {/* Brand Badge */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/80 border border-black/5 shadow-xs backdrop-blur-md">
            <Sparkles className="w-4 h-4 text-purple-600" />
            <span className="text-xs font-semibold tracking-wider uppercase text-purple-950">
              Infleuince Beta Access
            </span>
          </div>
        </div>

        {/* Card Container */}
        <div className="bg-white/80 backdrop-blur-xl border border-black/[0.08] rounded-3xl p-8 sm:p-10 shadow-xl shadow-purple-900/[0.03] transition-all">
          {status === 'success' ? (
            /* Success State */
            <div className="text-center py-6 space-y-4 animate-in fade-in zoom-in-95 duration-300">
              <div className="w-16 h-16 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center mx-auto mb-2">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight font-serif">
                You’re on the list!
              </h2>
              <p className="text-neutral-600 text-sm sm:text-base leading-relaxed">
                {responseMessage}
              </p>
              <div className="pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setStatus('idle');
                    setFormData({ firstName: '', lastName: '', email: '', source: 'website', referralCode: '' });
                  }}
                  className="text-xs font-semibold text-purple-600 hover:text-purple-800 underline underline-offset-4 cursor-pointer"
                >
                  Register another email
                </button>
              </div>
            </div>
          ) : (
            /* Form State */
            <div>
              <div className="text-center mb-8">
                <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight font-serif text-neutral-900 mb-3">
                  Join the Waitlist
                </h1>
                <p className="text-neutral-600 text-sm sm:text-base">
                  Get early access to the AI-powered content operating system for creators and brands.
                </p>
              </div>

              {status === 'error' && (
                <div className="mb-6 p-4 rounded-2xl bg-red-50 border border-red-200/80 text-red-700 text-sm flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <span>{errorMessage}</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
                      First Name
                    </label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      placeholder="Jane"
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-neutral-50 border border-neutral-200 text-neutral-900 placeholder:text-neutral-400 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-white transition"
                    />
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
                      Last Name
                    </label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      placeholder="Doe"
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl bg-neutral-50 border border-neutral-200 text-neutral-900 placeholder:text-neutral-400 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-white transition"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
                    Email Address <span className="text-purple-600">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    placeholder="jane@creator.com"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl bg-neutral-50 border border-neutral-200 text-neutral-900 placeholder:text-neutral-400 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-white transition"
                  />
                </div>

                <div>
                  <label htmlFor="referralCode" className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
                    Referral or VIP Code <span className="text-neutral-400 font-normal">(Optional)</span>
                  </label>
                  <input
                    type="text"
                    id="referralCode"
                    name="referralCode"
                    placeholder="e.g. BETA2026"
                    value={formData.referralCode}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-xl bg-neutral-50 border border-neutral-200 text-neutral-900 placeholder:text-neutral-400 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:bg-white transition"
                  />
                </div>

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full mt-2 py-3.5 px-6 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-semibold text-sm flex items-center justify-center gap-2 shadow-lg shadow-purple-600/20 active:scale-[0.99] transition disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
                >
                  {status === 'submitting' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Reserving spot...</span>
                    </>
                  ) : (
                    <>
                      <span>Get Early Access</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Footer info */}
        <p className="text-center text-xs text-neutral-400 mt-6">
          By joining, you agree to receive beta updates from Infleuince. Unsubscribe at any time.
        </p>
      </div>
    </div>
  );
}